import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Figure, InlineBar, RankedBars, Columns, Ledger } from "~/components/charts";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import type { AiImpactResponse, AiImpactDeveloperRow } from "@aura/shared";
import { buildImpactDek, buildImpactHeadline, idleSeats, mergeSpeedup } from "./story";
import { fmtDay, fmtHours, fmtNum, fmtUsd, fmtUsdShort, periodName } from "~/lib/format";
import { ed, tbl, AI_LINKS, Masthead } from "~/components/editorial";

type SortKey = "spend" | "aiShare" | "prs" | "name";

function SeatList({ seats }: { seats: AiImpactDeveloperRow["seats"] }) {
  const all = [
    { on: seats.claudeCode, label: "CC", title: "Claude Code seat" },
    { on: seats.cursor, label: "Cu", title: "Cursor seat" },
    { on: seats.copilot, label: "Co", title: "Copilot seat" },
  ];
  return (
    <span className={ed.num}>
      {all.map((s, i) => (
        <span key={s.label}>
          {i > 0 ? " " : ""}
          <span
            title={`${s.title}${s.on ? "" : ": none"}`}
            className={s.on ? undefined : ed.muted}
            style={s.on ? { fontWeight: 600 } : { textDecoration: "line-through" }}
          >
            {s.label}
          </span>
        </span>
      ))}
    </span>
  );
}

export function AIImpactPage() {
  const { range, window: periodWin } = usePeriod();
  const [sort, setSort] = useState<SortKey>("spend");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const { data: response, isLoading, error } = useQuery({
    queryKey: ["ai-impact", range],
    queryFn: () => api.get<{ data: AiImpactResponse }>(`/ai-impact?range=${range}`),
    staleTime: 5 * 60 * 1000,
  });

  const summary = response?.data.summary;
  const trend = response?.data.trend ?? [];
  const repos = response?.data.repos ?? [];
  const now = periodName(periodWin.label);

  const developers = useMemo(() => {
    const rows = response?.data.developers ?? [];
    return [...rows].sort((a, b) => {
      let cmp: number;
      if (sort === "spend") cmp = a.spend.totalUsd - b.spend.totalUsd;
      else if (sort === "aiShare") cmp = a.aiPrSharePct - b.aiPrSharePct;
      else if (sort === "prs") cmp = a.prs.total - b.prs.total;
      else cmp = a.displayName.localeCompare(b.displayName);
      return order === "asc" ? cmp : -cmp;
    });
  }, [response, sort, order]);

  const handleSort = (key: SortKey) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setOrder(key === "name" ? "asc" : "desc");
    }
  };

  const sortHead = (key: SortKey, label: string, num = true) => (
    <th
      data-num={num || undefined}
      data-active={sort === key || undefined}
      aria-sort={sort === key ? (order === "asc" ? "ascending" : "descending") : "none"}
    >
      <button type="button" onClick={() => handleSort(key)}>
        {label}
        {sort === key ? (order === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );

  const idle = summary ? idleSeats(summary) : 0;
  const speedup = summary ? mergeSpeedup(summary) : null;
  const maxSpend = Math.max(1, ...developers.map((d) => d.spend.totalUsd));
  const peakWeek = trend.reduce<(typeof trend)[number] | null>(
    (best, t) => (best === null || t.aiSharePct > best.aiSharePct ? t : best),
    null,
  );

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead links={AI_LINKS} meta={periodWin.label} />

        {isLoading ? (
          <div className={ed.loading}>Loading AI impact…</div>
        ) : error || !summary ? (
          <div className={ed.loading}>
            Couldn't load AI impact{error ? `: ${String((error as Error).message)}` : "."}
          </div>
        ) : (
          <>
            <div className={ed.kicker}>AI tooling · {now}</div>
            <h1 className={ed.headline}>{buildImpactHeadline(summary, periodWin.label)}</h1>
            <p className={ed.dek}>{buildImpactDek(summary)}</p>
            <div className={ed.bylineSplit}>
              <span>
                <b>AI impact</b> · spend from the Anthropic, Cursor and GitHub admin APIs joined to
                PRs and commits from GitHub. Copilot is estimated from seat price.
              </span>
              <Link to="/ai-usage">Spend detail by person →</Link>
            </div>

            {!summary.spend.claudeCodeAvailable && (
              <div className={ed.notice}>
                <b>Claude Code spend is not connected.</b> Set ANTHROPIC_ADMIN_API_KEY on the server.
                Totals and cost per PR exclude Claude Code until then.
              </div>
            )}

            <Ledger
              hideMissingPrior
              items={[
                { label: "AI spend", value: fmtUsd(summary.spend.totalUsd), note: "all tools, period" },
                {
                  label: "PRs AI-assisted",
                  value: `${summary.output.aiPrSharePct}%`,
                  note: `${fmtNum(summary.output.aiAssistedPrs)} of ${fmtNum(summary.output.totalPrs)}`,
                },
                { label: "Cost per AI PR", value: fmtUsd(summary.costPerAiPr), note: "spend / AI-assisted PRs" },
                {
                  label: "Adoption",
                  value: `${summary.adoption.adoptionPct}%`,
                  note: `${fmtNum(summary.adoption.developersUsingAi)} of ${fmtNum(summary.adoption.activeDevelopers)} active devs`,
                },
                {
                  label: "Idle seats",
                  value: fmtNum(idle),
                  note: "paid, no usage",
                  valueFlag: idle > 0 ? "caution" : undefined,
                },
                {
                  label: "Merge time, AI",
                  value: fmtHours(summary.cycleTime.aiAvgHours),
                  note:
                    speedup === null
                      ? "no comparison yet"
                      : `vs ${fmtHours(summary.cycleTime.manualAvgHours)} manual (${speedup > 0 ? "−" : "+"}${Math.abs(speedup)}%)`,
                },
              ]}
            />

            <div className={ed.sect}>I. Adoption</div>
            <h2 className={ed.h2}>
              {peakWeek
                ? `AI-assisted share peaked at ${peakWeek.aiSharePct}% in the week of ${fmtDay(peakWeek.date)}`
                : "No PRs in this period"}
            </h2>
            {trend.length > 0 ? (
              <Figure
                title="PRs per week, AI-assisted in color"
                sub="Share of each week's PRs written with an AI tool, above each column."
                method="PRs opened in the period, grouped into weeks starting Monday. AI-assisted: an AI tool is named in the PR title, body or commit messages (Claude, Copilot, Cursor and others)."
                table={{
                  columns: ["Week of", "PRs", "AI-assisted", "Share"],
                  rows: trend.map((t) => [fmtDay(t.date), t.totalPrs, t.aiPrs, `${t.aiSharePct}%`]),
                }}
              >
                <Columns
                  ariaLabel="PRs per week, AI-assisted and not"
                  valueLabel="AI-assisted"
                  stackLabel="Other PRs"
                  tone="emph"
                  formatKey={fmtDay}
                  format={(v) => String(Math.round(v))}
                  tickFormat={(v) => String(Math.round(v))}
                  height={170}
                  margin={{ t: 24, l: 52 }}
                  steps={2}
                  barRatio={0.68}
                  maxBarWidth={48}
                  points={trend.map((t) => ({
                    key: t.date,
                    value: t.aiPrs,
                    stack: Math.max(0, t.totalPrs - t.aiPrs),
                    note: `${t.aiSharePct}%`,
                  }))}
                />
              </Figure>
            ) : (
              <p className={ed.prose}>No PRs in this period.</p>
            )}

            <div className={ed.sect}>II. Spend and seats</div>
            <h2 className={ed.h2}>
              {fmtUsd(summary.spend.totalUsd)} across three tools
              {idle > 0 ? `, with ${fmtNum(idle)} ${idle === 1 ? "seat" : "seats"} unused` : ""}
            </h2>
            <div className={ed.twoUp}>
              <Figure
                title="Spend by tool"
                sub="The largest line in color."
                method="Claude Code and Cursor are billed usage from their admin APIs. Copilot is seats times list price, an estimate."
              >
                <RankedBars
                  ariaLabel="Spend by tool"
                  format={fmtUsdShort}
                  rows={[
                    { id: "cc", label: "Claude Code", value: summary.spend.claudeCodeUsd ?? 0 },
                    { id: "cu", label: "Cursor", value: summary.spend.cursorUsd },
                    { id: "co", label: "Copilot (est.)", value: summary.spend.copilotEstimateUsd },
                  ]
                    .sort((a, b) => b.value - a.value)
                    .map((r, i) => ({ ...r, emphasis: i === 0 && r.value > 0 }))}
                  width={560}
                />
              </Figure>
              <div>
                <h3 className={ed.h3Sm} style={{ marginTop: 0 }}>
                  Seats
                </h3>
                <div className={tbl.scroll}>
                  <table className={tbl.table}>
                    <thead>
                      <tr>
                        <th>Tool</th>
                        <th data-num>Seats</th>
                        <th data-num>Idle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Claude Code", summary.seats.claudeCode.total, summary.seats.claudeCode.idle],
                        ["Cursor", summary.seats.cursor.total, summary.seats.cursor.idle],
                        ["Copilot", summary.seats.copilot.total, summary.seats.copilot.idle],
                      ].map(([tool, total, idleN]) => (
                        <tr key={String(tool)}>
                          <td>{tool}</td>
                          <td data-num>{fmtNum(total as number)}</td>
                          <td data-num data-flag={(idleN as number | null) ? true : undefined}>
                            {idleN === null ? "?" : fmtNum(idleN as number)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={ed.methodMono}>
                  Idle: a paid seat with no recorded use in the period. Claude Code idle is unknown
                  without the admin key.
                </p>
              </div>
            </div>

            <div className={ed.sect}>III. By developer</div>
            <h2 className={ed.h2}>What each person spent and shipped</h2>
            <p className={ed.prose}>
              <span className="num">{fmtNum(developers.length)}</span> people with an AI seat or code
              activity in {now}. The bar shows spend against the largest.
            </p>
            {developers.length === 0 ? (
              <p className={ed.small}>No developers with AI seats or activity in this period.</p>
            ) : (
              <div className={tbl.scroll}>
                <table className={tbl.table}>
                  <thead>
                    <tr>
                      {sortHead("name", "Developer", false)}
                      <th title="CC Claude Code, Cu Cursor, Co Copilot; struck through means no seat">
                        Seats
                      </th>
                      {sortHead("spend", "Spend")}
                      <th aria-hidden />
                      {sortHead("prs", "PRs AI / all")}
                      <th data-num>Commits AI / all</th>
                      {sortHead("aiShare", "AI share")}
                    </tr>
                  </thead>
                  <tbody>
                    {developers.map((dev) => (
                      <tr key={dev.developerId}>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <Link to={`/developers/${dev.developerId}`}>{dev.displayName}</Link>
                          {!dev.active && <span className={tbl.sub}>no code activity</span>}
                        </td>
                        <td>
                          <SeatList seats={dev.seats} />
                        </td>
                        <td data-num>{fmtUsd(dev.spend.totalUsd)}</td>
                        <td>
                          <InlineBar value={dev.spend.totalUsd} max={maxSpend} width={56} />
                        </td>
                        <td data-num>
                          {dev.prs.aiAssisted} / {dev.prs.total}
                        </td>
                        <td data-num>
                          {dev.commits.aiAssisted} / {dev.commits.total}
                        </td>
                        <td data-num>
                          <span className={dev.prs.total === 0 ? ed.muted : undefined}>
                            {dev.aiPrSharePct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className={ed.sect}>IV. Readiness and adoption</div>
            <h2 className={ed.h2}>Which repositories are ready for AI, and which use it</h2>
            <p className={ed.prose}>
              Repositories with a high readiness score but a low AI share are the cheapest adoption
              wins.
            </p>
            {repos.length === 0 ? (
              <p className={ed.small}>No repos with PRs in this period.</p>
            ) : (
              <div className={tbl.scroll}>
                <table className={tbl.table}>
                  <thead>
                    <tr>
                      <th>Repository</th>
                      <th data-num title="0 to 100 AI-readiness score from the latest repo audit">
                        Readiness
                      </th>
                      <th data-num>PRs</th>
                      <th data-num>AI-assisted</th>
                      <th data-num>AI share</th>
                      <th aria-hidden />
                    </tr>
                  </thead>
                  <tbody>
                    {repos.map((repo) => (
                      <tr key={repo.repositoryId}>
                        <td data-mono>{repo.name}</td>
                        <td data-num>
                          <span className={repo.readinessScore == null ? ed.muted : undefined}>
                            {repo.readinessScore ?? "not scanned"}
                          </span>
                        </td>
                        <td data-num>{fmtNum(repo.prCount)}</td>
                        <td data-num>{fmtNum(repo.aiAssistedPrCount)}</td>
                        <td data-num>{repo.aiPrSharePct}%</td>
                        <td>
                          <InlineBar value={repo.aiPrSharePct} max={100} width={56} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className={ed.methodMono}>
              PRs opened in the period per repository. Readiness comes from the latest repo audit.
              Copilot spend is estimated from seat price; Cursor and Claude Code are actual.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
